import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import {
  useAddEventMutation,
  useGetEventById,
  useGetFixedAssetEventDataMutation,
  useGetLastFixedAssetDeprecationDate,
} from "src/api/purchasing/events";
import { useGetBranches, useGetPaymentMethods } from "src/api/generic";
import { useTranslation } from "react-i18next";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { convertObjectToFormData, isEqualRoundedDecimals } from "src/utils";
import { useGetCapitalList } from "src/api/settings/capital";
import { useGetEventsFixedAssetsAccountsList } from "src/api/operation-cost/accounts";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useGetTreeFixedAssetAccountsList } from "src/api/tree/tree";
import AddMoreIcon from "src/assets/svg-icons/settings/add_more.svg";
import { authState } from "src/store/auth";
import { ConfirmPopup } from "src/shared/components/confirm-popup";

interface Props {
  open: boolean;
  handleClose: () => void;
  refetch: Function;
  eventId?: string;
}

const schema = yup.object({
  mode: yup
    .string()
    .required("required")
    .oneOf(["add", "view"] as const),
  branch_id: yup.number().required("Required"),
  amount: yup.string().required("Required"),
  account_id: yup.string().required("Required"),
  date: yup.date().required("required"),
  note: yup.string(),
  payments: yup
    .array()
    .of(
      yup.object().shape({
        payment_method_id: yup
          .string()
          .test("payment-method-required", "required", function (value) {
            const { amount } = this.parent;
            if (amount && !value) {
              return false;
            }
            return true;
          }),
        amount: yup
          .string()
          .test("payment-amount-required", "required", function (value) {
            const { payment_method_id } = this.parent;
            if (payment_method_id && !value) {
              return false;
            }
            return true;
          })
          .test("min-value", "Must be greater than 0", (value) => {
            if (!value) return true; // Skip validation if the value is empty
            return parseFloat(value as string) > 0;
          }),
      }),
    )
    .required("Required"),
  owners: yup
    .array()
    .of(
      yup.object().shape({
        owner_id: yup
          .string()
          .test("owner-method-required", "required", function (value) {
            const { amount } = this.parent;
            if (amount && !value) {
              return false;
            }
            return true;
          }),
        amount: yup
          .string()
          .test("owner-amount-required", "required", function (value) {
            const { owner_id } = this.parent;
            if (owner_id && !value) {
              return false;
            }
            return true;
          })
          .test("min-value", "Must be greater than 0", (value) => {
            if (!value) return true; // Skip validation if the value is empty
            return parseFloat(value as string) > 0;
          }),
      }),
    )
    .required("Required"),
});

type FormInputs = yup.InferType<typeof schema>;

const FixedAssetEventPopup = ({
  open,
  handleClose,
  refetch,
  eventId,
}: Props) => {
  const { t } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");
  const { t: OCT } = useTranslation("operationCost");
  const [files, setFiles] = useState<any>([]);
  const auth = useRecoilValue(authState);
  const company = auth?.user?.company;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [openConfirm, setOpenConfirm] = useState(false);

  // apis
  const { mutate, isPending, status, error } = useAddEventMutation();
  const { data: capitalData } = useGetCapitalList();
  const { data: branchesData } = useGetBranches();
  const { data: treeFixedAssetAccounts } = useGetTreeFixedAssetAccountsList();
  const { mutate: mutateGetFixedAssetData, data: assetCalc } =
    useGetFixedAssetEventDataMutation();
  const { data: paymentsData } = useGetPaymentMethods();
  const { data: eventData, isFetching: isFetchingEvent } = useGetEventById(
    `${eventId}`,
  );
  const { data: lastDeprecationData, isLoading } =
    useGetLastFixedAssetDeprecationDate();
  const lastDeprecationDate = lastDeprecationData?.data?.deprecation_date;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setError,
    setValue,
  } = useForm<FormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      mode: "add",
      payments: [
        {
          payment_method_id: "",
          amount: "",
        },
      ],
      owners: [
        {
          owner_id: "",
          amount: "",
        },
      ],
    },
  });

  const { data: accountData } = useGetEventsFixedAssetsAccountsList({
    branch_id: watch("branch_id"),
    date: watch("date") ? moment(watch("date")).format("YYYY-MM-DD") : "",
  });

  const { fields, append, remove } = useFieldArray({
    name: "payments",
    control,
  });

  const {
    fields: ownersFields,
    append: appendOwner,
    remove: removeOwner,
  } = useFieldArray({
    name: "owners",
    control,
  });

  const mode = watch("mode");
  const branchId = watch("branch_id");

  const originalCost =
    mode === "add"
      ? (assetCalc?.data?.original_cost ?? 0)
      : eventData?.data?.purchase_event_balance?.original_cost;
  const accumulatedDepreciation =
    mode === "add"
      ? (assetCalc?.data?.accumulated_depreciation ?? 0)
      : eventData?.data?.purchase_event_balance?.accumulated_depreciation;

  const netBookValue =
    mode === "add"
      ? originalCost - accumulatedDepreciation
      : eventData?.data?.purchase_event_balance?.net_book_value;

  const date = watch("date");
  const fixedAssetId = watch("account_id");
  const amount = watch("amount");

  const selectedAsset = accountData?.data?.find(
    (item: any) => item?.id === fixedAssetId,
  );

  const key =
    amount > netBookValue
      ? "sale_of_fixed_assets"
      : "loss_on_sale_of_fixed_assets";

  const treeAccount = treeFixedAssetAccounts?.data?.find(
    (account: any) => account?.system_key === key,
  );

  console.log({ selectedAsset, treeFixedAssetAccounts });

  const handleCloseConfirm = () => setOpenConfirm(false);
  const handleOpenConfirm = () => {
    if (mode === "add") {
      setOpenConfirm(true);
    } else {
      onClose();
    }
  };

  const onClose = () => {
    handleClose();
    reset({});
  };

  const handleAppendPayment = () => {
    append({
      payment_method_id: "",
      amount: "",
    });
  };

  const handleRemovePayment = (index: number) => {
    remove(index);
  };

  const handleAppendOwner = () => {
    appendOwner({
      owner_id: "",
      amount: "",
    });
  };

  const handleRemoveOwner = (index: number) => {
    removeOwner(index);
  };

  const onSubmit = (data: FormInputs) => {
    const { payments, amount, owners } = data;

    if (
      lastDeprecationDate &&
      moment(data.date)
        .startOf("day")
        .isSameOrBefore(moment(lastDeprecationDate).startOf("day"))
    ) {
      setError("date", {
        type: "manual",
        message: t("depreciationDateError", { date: lastDeprecationDate }),
      });
      return;
    }

    // Balance date validation
    if (
      !!company?.start_balance_date &&
      moment(data.date)
        .startOf("day")
        .isBefore(moment(company.start_balance_date).startOf("day"))
    ) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: `${generalT("balanceDateValidationMsg")}: ${
            company?.start_balance_date ?? ""
          }`,
        },
      ]);
      return;
    }

    const paymentsAmount = payments.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0,
    );
    const ownersAmount = owners.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0,
    );
    const totalAmount = paymentsAmount + ownersAmount;
    if (totalAmount !== +amount) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: `${t("fixedAssetSplitTotalMismatch")}`,
        },
      ]);
      return;
    }

    const payload = {
      type: "fixed_asset",
      branch_id: +data.branch_id,
      method: "fixed_asset",
      amount,
      date: moment(data.date).format("YYYY-MM-DD"),
      account_id: data.account_id,
      chart_account_id: treeAccount?.id,
      ...(data.note && { note: data.note }),
    };

    const formData = convertObjectToFormData(payload);

    // append asset data
    formData.append(
      `purchase_event_balance[balance]`,
      selectedAsset.balance ?? "0",
    );
    formData.append(
      `purchase_event_balance[original_cost]`,
      assetCalc?.data?.original_cost ?? "0",
    );
    formData.append(
      `purchase_event_balance[accumulated_depreciation]`,
      assetCalc?.data?.accumulated_depreciation ?? "0",
    );
    formData.append(
      `purchase_event_balance[net_book_value]`,
      `${netBookValue}`,
    );

    // append payments array
    payments.forEach((payment, index: number) => {
      if (payment.payment_method_id && payment.amount && +payment?.amount > 0) {
        formData.append(
          `payment_method_payments[${index}][payment_method_id]`,
          payment.payment_method_id,
        );
        formData.append(
          `payment_method_payments[${index}][amount]`,
          payment.amount,
        );
      }
    });

    // append owners array
    owners.forEach((owner, index: number) => {
      if (owner.owner_id && owner.amount && +owner?.amount > 0) {
        formData.append(
          `capital_payments[${index}][capital_id]`,
          owner.owner_id,
        );
        formData.append(`capital_payments[${index}][amount]`, owner.amount);
      }
    });

    // append files
    if (files.length > 0) {
      files.forEach((file: any, index: number) => {
        formData.append(`files[${index}]`, file);
      });
    }

    mutate(formData, {
      onSuccess: () => {
        refetch();
        onClose();
      },
      onError: (error) => {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message:
              error?.data?.errors?.[0].value || generalT("somethingWrong"),
          },
        ]);
      },
    });
  };

  useEffect(() => {
    if (date && fixedAssetId && branchId) {
      mutateGetFixedAssetData({
        date: moment(date).format("YYYY-MM-DD"),
        account_id: +fixedAssetId,
        branch_id: branchId,
      });
    }
  }, [date, fixedAssetId, branchId]);

  useEffect(() => {
    if (mode === "add") {
      setValue("account_id", "");
    }
  }, [watch("branch_id"), watch("date")]);

  useEffect(() => {
    if (eventData) {
      const data = eventData?.data;
      reset({
        mode: "view",
        branch_id: data.branch?.id,
        amount: data.amount,
        account_id: data.account?.id,
        date: moment(data.date, "YYYY-MM-DD").toDate(),
        note: data.note ?? "",
        owners: data.capital_payments.map((capital: any) => {
          return {
            owner_id: capital.capital_id,
            amount: capital.amount,
          };
        }),
        payments: data.payment_method_payments.map((payment: any) => {
          return {
            payment_method_id: payment.payment_method_id,
            amount: payment.amount,
          };
        }),
      });
    }
  }, [eventData]);

  //add success and error handling
  useEffect(() => {
    if (status === "success") {
      refetch();
      onClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <Box>
      <Modal open={open}>
        <Stack
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "100%", lg: "737px" },
            bgcolor: "#FFF",
            border: "1px solid #D0D5DD",
            borderRadius: 1,
            boxShadow: 24,
            p: 3,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography fontSize="20px">{t("fixedAssetSale")}</Typography>
            <IconButton onClick={handleOpenConfirm} sx={{ p: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {(!!eventId && isFetchingEvent) || isLoading ? (
            <Box
              minHeight={"500px"}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap={2}>
                <FormControl fullWidth size="small" error={!!errors.branch_id}>
                  <InputLabel id="demo-simple-select-label">
                    {generalT("branch")}
                  </InputLabel>
                  <Controller
                    name="branch_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label={generalT("branch")}
                        disabled={mode === "view"}
                      >
                        {branchesData?.data?.map(({ id, name }) => {
                          return (
                            <MenuItem key={id} value={`${id}`}>
                              {name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {errors.branch_id?.message}
                  </FormHelperText>
                </FormControl>
                {/* Date */}
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      format={"dd/MM/yyyy"}
                      disabled={mode === "view"}
                      label={`${t("transactionDateRepeat")}`}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: !!errors.date,
                          helperText: errors.date?.message,
                          inputProps: {
                            readOnly: true,
                            onKeyDownCapture: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                            onPasteCapture: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                          },
                        },
                      }}
                    />
                  )}
                />

                {/* fixed asset */}
                {mode == "add" ? (
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.account_id}
                    sx={{ mt: 1 }}
                  >
                    <InputLabel id="demo-simple-select-label">
                      {t("fixedAsset")}
                    </InputLabel>
                    <Controller
                      name="account_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          key={field.value}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label={t("fixedAsset")}
                        >
                          {accountData?.data.map((account: any) => {
                            return (
                              <MenuItem key={account.id} value={account.id}>
                                {account.name}
                              </MenuItem>
                            );
                          })}
                          {accountData?.data?.length === 0 ? (
                            <Typography align="center" color={"textSecondary"}>
                              {generalT("noData")}
                            </Typography>
                          ) : null}
                        </Select>
                      )}
                    />
                    <FormHelperText id="my-helper-text">
                      {errors.account_id?.message}
                    </FormHelperText>
                  </FormControl>
                ) : (
                  <TextField
                    size="small"
                    label={t("fixedAsset")}
                    fullWidth
                    disabled
                    value={eventData?.data?.account?.name ?? ""}
                  />
                )}

                <Controller
                  name={"amount"}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      label={t("amountTotal")}
                      variant="outlined"
                      {...field}
                      error={!!errors?.amount}
                      fullWidth
                      size="small"
                      disabled={mode === "view"}
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />

                {+amount > 0 &&
                !isEqualRoundedDecimals(+amount, netBookValue ?? 0, 2) ? (
                  <TextField
                    label="Account"
                    value={treeAccount?.name ?? ""}
                    disabled
                    size="small"
                    fullWidth
                  />
                ) : null}

                <Grid container spacing={2}>
                  <Grid item xs={6} md={4}>
                    <Box
                      p={"10px"}
                      border={"1px solid #dedcdc"}
                      bgcolor={"#dedcdc"}
                      borderRadius={"8px"}
                    >
                      <Typography fontSize={"14px"}>
                        {t("originalCost")}
                      </Typography>
                      <Typography fontSize={"14px"} fontWeight={600}>
                        {originalCost.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Box
                      p={"10px"}
                      border={"1px solid #dedcdc"}
                      bgcolor={"#dedcdc"}
                      borderRadius={"8px"}
                    >
                      <Typography fontSize={"14px"}>
                        {t("accumulatedDepreciation")}
                      </Typography>
                      <Typography fontSize={"14px"} fontWeight={600}>
                        {accumulatedDepreciation?.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Box
                      p={"10px"}
                      border={"1px solid #dedcdc"}
                      bgcolor={"#dedcdc"}
                      borderRadius={"8px"}
                    >
                      <Typography fontSize={"14px"}>
                        {OCT("netBookValue")}
                      </Typography>
                      <Typography fontSize={"14px"} fontWeight={600}>
                        {netBookValue.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* payments */}
                {fields.map((paymentField, index) => {
                  return (
                    <Stack key={paymentField.id} direction={"row"} gap={1}>
                      <FormControl
                        fullWidth
                        size="small"
                        error={
                          errors.payments &&
                          !!errors.payments[index]?.payment_method_id
                        }
                      >
                        <InputLabel id="demo-simple-select-label">
                          {t("paymentMethodOptional")}
                        </InputLabel>
                        <Controller
                          name={`payments.${index}.payment_method_id`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              key={field.value}
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              label={t("paymentMethodOptional")}
                              disabled={mode === "view"}
                            >
                              {paymentsData?.data
                                ?.filter((pay) => pay.status)
                                .map(({ id, name }) => {
                                  return (
                                    <MenuItem key={id} value={`${id}`}>
                                      {name}
                                    </MenuItem>
                                  );
                                })}
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {errors.payments &&
                            errors.payments[index]?.payment_method_id?.message}
                        </FormHelperText>
                      </FormControl>

                      <Controller
                        name={`payments.${index}.amount`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={t("amount")}
                            variant="outlined"
                            {...field}
                            error={
                              errors.payments &&
                              !!errors.payments[index]?.amount
                            }
                            helperText={
                              errors.payments &&
                              errors.payments[index]?.amount?.message
                            }
                            fullWidth
                            size="small"
                            disabled={mode === "view"}
                          />
                        )}
                      />

                      {mode === "add" && (
                        <IconButton onClick={() => handleRemovePayment(index)}>
                          <img src={DeleteIcon} alt="" />
                        </IconButton>
                      )}
                    </Stack>
                  );
                })}
                {mode === "add" && (
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<img src={AddMoreIcon} alt="" />}
                      onClick={handleAppendPayment}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {t("addAnotherMethod")}
                    </Button>
                  </Stack>
                )}

                {/* owners */}
                {ownersFields.map((ownerField, index) => {
                  return (
                    <Stack key={ownerField.id} direction={"row"} gap={1}>
                      <FormControl
                        fullWidth
                        size="small"
                        error={!!errors.owners?.[index]?.owner_id}
                      >
                        <InputLabel id="demo-simple-select-label">
                          {t("owner")}
                        </InputLabel>
                        <Controller
                          name={`owners.${index}.owner_id`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              key={field.value}
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              label={t("owner")}
                              disabled={mode === "view"}
                            >
                              {capitalData?.data.map((owner: any) => {
                                return (
                                  <MenuItem
                                    key={owner.id}
                                    value={`${owner.id}`}
                                  >
                                    {owner.name}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {errors.owners?.[index]?.owner_id?.message}
                        </FormHelperText>
                      </FormControl>

                      <Controller
                        name={`owners.${index}.amount`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={t("amount")}
                            variant="outlined"
                            {...field}
                            error={!!errors.owners?.[index]?.amount}
                            helperText={errors.owners?.[index]?.amount?.message}
                            fullWidth
                            size="small"
                            disabled={mode === "view"}
                          />
                        )}
                      />

                      {mode === "add" && (
                        <IconButton onClick={() => handleRemoveOwner(index)}>
                          <img src={DeleteIcon} alt="" />
                        </IconButton>
                      )}
                    </Stack>
                  );
                })}
                {mode === "add" && (
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<img src={AddMoreIcon} alt="" />}
                      onClick={handleAppendOwner}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {t("addAnotherOwner")}
                    </Button>
                  </Stack>
                )}

                {/* Note */}
                <Controller
                  name="note"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label={t("notesOptional")}
                      fullWidth
                      multiline
                      rows={3}
                      sx={{ mt: 1 }}
                      disabled={mode === "view"}
                    />
                  )}
                />

                {mode === "add" && (
                  <FilePondUploader
                    onUpload={(e) => setFiles(e)}
                    maxFiles={3}
                    maxFileSize={3}
                    acceptedFileTypes={["image/*", "application/pdf"]}
                  />
                )}
              </Stack>

              {mode === "add" && (
                <Stack direction="row" spacing={2} mt={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleOpenConfirm}
                  >
                    {generalT("cancel")}
                  </Button>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    fullWidth
                    loading={isPending}
                  >
                    {generalT("submit")}
                  </LoadingButton>
                </Stack>
              )}
            </form>
          )}
        </Stack>
      </Modal>
      {/* confirm close popup */}
      <ConfirmPopup
        open={openConfirm}
        handleClose={handleCloseConfirm}
        handleConfirm={onClose}
        title={generalT("discard")}
        subtitle={generalT("areYouSureToDiscardChanges")}
        confirmBtnText={generalT("confirm")}
      />
    </Box>
  );
};

export default FixedAssetEventPopup;
